/**
 * Project view header (§7g Commit E).
 *
 * Renders the project name + description + an archive (delete) button.
 * Archive uses a confirm prompt — the backend does a soft-delete so
 * the row stays interpretable for any existing conversations, but the
 * user experience is "gone from the list" per §7.8 Q3.
 *
 * v4.1 Phase 36.9: adds the member avatar stack, "View only" badge for
 * non-editors, and a "Manage members" button that opens the dialog.
 * Entire surface gated by COLLAB_MODE_ENABLED.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../store/useStore.js";
import { getUserInfo } from "../services/auth.js";
import { COLLAB_MODE_ENABLED } from "../config.js";
import ManageMembersDialog from "./project/ManageMembersDialog.jsx";
import ShareDialog from "./project/ShareDialog.jsx";

function memberInitials(userId) {
  if (!userId) return "?";
  return userId.slice(0, 2).toUpperCase();
}

function MemberAvatarStack({ ownerId, members, max = 3 }) {
  // Owner + up to `max` members inline, then a "+N" overflow chip.
  const visible = useMemo(() => {
    const entries = [{ userId: ownerId, role: "owner" }];
    for (const m of members || []) {
      entries.push(m);
    }
    return entries.slice(0, max + 1);
  }, [ownerId, members, max]);
  const overflow = Math.max(0, (members?.length || 0) + 1 - visible.length);
  return (
    <div className="member-avatar-stack" aria-label="Project members">
      {visible.map((entry) => (
        <div
          key={entry.userId}
          className="member-avatar"
          title={`${entry.userId} (${entry.role})`}
        >
          {memberInitials(entry.userId)}
        </div>
      ))}
      {overflow > 0 && (
        <div className="member-avatar-overflow" title={`+${overflow} more`}>
          +{overflow}
        </div>
      )}
    </div>
  );
}

function effectiveRole(project, callerSub) {
  if (!project || !callerSub) return null;
  if (project.ownerId === callerSub) return "owner";
  const member = (project.members || []).find((m) => m.userId === callerSub);
  if (member) return member.role;
  return null;
}

export default function ProjectHeader({ project }) {
  const navigate = useNavigate();
  const archiveProject = useStore((s) => s.archiveProject);
  const pushToast = useStore((s) => s.pushToast);
  const callerSub = useMemo(() => getUserInfo()?.sub ?? null, []);
  const [archiving, setArchiving] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const role = effectiveRole(project, callerSub);
  const isOwner = role === "owner";
  const canArchive = isOwner;
  const isViewer = role === "viewer";

  const handleArchive = async () => {
    if (!confirm(`Archive project "${project.name}"? This hides it from your list.`)) {
      return;
    }
    setArchiving(true);
    try {
      await archiveProject(project.id);
      pushToast(`Project "${project.name}" archived`, "success");
      navigate("/");
    } catch (err) {
      pushToast(`Failed to archive: ${err.message}`, "error");
      setArchiving(false);
    }
  };

  return (
    <div className="project-header">
      <div className="project-header-main">
        <h1 className="project-header-title">{project.name}</h1>
        {project.companyUrl && (
          <a
            className="project-header-company-url"
            href={project.companyUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {project.companyUrl.replace(/^https?:\/\//, "")}
          </a>
        )}
        {project.description && (
          <p className="project-header-description">{project.description}</p>
        )}
        {COLLAB_MODE_ENABLED && (
          <div className="project-header-members">
            <MemberAvatarStack
              ownerId={project.ownerId}
              members={project.members}
            />
            {isViewer && (
              <span className="view-only-banner" aria-label="View only role">
                View only
              </span>
            )}
          </div>
        )}
      </div>
      <div className="project-header-actions">
        {COLLAB_MODE_ENABLED && role && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setShareOpen(true)}
          >
            Share
          </button>
        )}
        {COLLAB_MODE_ENABLED && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setManageOpen(true)}
          >
            Manage members
          </button>
        )}
        {canArchive && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleArchive}
            disabled={archiving}
          >
            {archiving ? "Archiving…" : "Archive"}
          </button>
        )}
      </div>
      {COLLAB_MODE_ENABLED && (
        <ManageMembersDialog
          project={project}
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          role={role}
          onOwnershipTransferred={() => navigate("/")}
        />
      )}
      {COLLAB_MODE_ENABLED && (
        <ShareDialog
          project={project}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}
